import { getMainAndSiderSpans } from '@frontend/components/hooks'
import { registerInDevtools, Store } from 'pullstate'

import { InboundOutboundType } from '@shared/const'

import { Identifier, Nullable } from '@shared-types/util'

import {
  BookShipmentSideBarState,
  BookShipmentStep,
  CheckInShipmentState,
  CheckInShipmentStep,
  ChecklistDetailsState,
  CreateChecklistState,
  CreateSlotSideBarState,
  CreateVoucherState,
  EditSlotSideBarState,
  ExtractionRunDetailsState,
  SelectedShipmentWithStatus,
  ShipmentDetailsSideBarState,
  SideColumnType,
  VisitDetailsState,
  VoucherDetailsState,
} from './types'

type SideBarState = Nullable<
  | ShipmentDetailsSideBarState
  | CreateSlotSideBarState
  | BookShipmentSideBarState
  | EditSlotSideBarState
  | CreateVoucherState
  | CreateChecklistState
  | ChecklistDetailsState
  | VoucherDetailsState
  | CheckInShipmentState
  | VisitDetailsState
  | ExtractionRunDetailsState
>

type SideColumnData<T> = T extends 'edit_slot'
  ? EditSlotSideBarState
  : T extends 'create_slot'
    ? CreateSlotSideBarState
    : T extends 'shipment_details'
      ? ShipmentDetailsSideBarState
      : T extends 'book_shipment'
        ? BookShipmentSideBarState
        : T extends 'create_voucher'
          ? CreateVoucherState
          : T extends 'checklist_details'
            ? ChecklistDetailsState
            : T extends 'create_checklist'
              ? CreateChecklistState
              : T extends 'voucher_details'
                ? VoucherDetailsState
                : T extends 'check_in_shipment'
                  ? CheckInShipmentState
                  : T extends 'visit_details'
                    ? VisitDetailsState
                    : T extends 'extraction_run_details'
                      ? ExtractionRunDetailsState
                      : never

type ShipmentDashboardStoreData = {
  sideColumnState: SideBarState
  sideColumnSplitterSize: Nullable<number>
}

const initialShipmentDashboardStore: ShipmentDashboardStoreData = {
  sideColumnState: null,
  sideColumnSplitterSize: null,
}

const GlobalSideColumnStore = new Store(initialShipmentDashboardStore)

function setSideColumnState(value: SideBarState) {
  GlobalSideColumnStore.update((s) => {
    s.sideColumnState = value
  })
}

export function closeSideColumn() {
  GlobalSideColumnStore.update((s) => {
    s.sideColumnState = null
  })
}

export function useSpecificSideColumnState<T extends SideColumnType>(columnType: T) {
  const sideColumnState = GlobalSideColumnStore.useState((s) => s.sideColumnState)

  if (sideColumnState?.type === columnType) {
    return sideColumnState as SideColumnData<T>
  }

  return null
}

export function useSideColumnState() {
  return GlobalSideColumnStore.useState((s) => s.sideColumnState)
}

export function setSideColumnSplitterSize(size: number) {
  GlobalSideColumnStore.update((s) => {
    s.sideColumnSplitterSize = size
  })
}

export function useSideColumnSplitterSize() {
  return GlobalSideColumnStore.useState((s) => s.sideColumnSplitterSize)
}

export function openCreateSlotColumn(facilityId: Identifier) {
  setSideColumnState({ type: 'create_slot', facilityId })
}

export function openEditSlotColumn(
  facilityId: Identifier,
  slotId: Identifier,
  shipmentType: InboundOutboundType
) {
  setSideColumnState({ type: 'edit_slot', facilityId, selectedSlotId: slotId, shipmentType })
}

export function openCreateVoucherColumn() {
  setSideColumnState({ type: 'create_voucher' })
}

export function openVoucherDetailsColumn(voucherId: Identifier) {
  setSideColumnState({ type: 'voucher_details', selectedVoucherId: voucherId })
}

export function openChecklistDetailsColumn({
  nid,
  checklistId,
  facilityId,
}: {
  nid: string
  checklistId: Identifier
  facilityId: Identifier
}) {
  setSideColumnState({
    type: 'checklist_details',
    selectedChecklistId: checklistId,
    selectedNid: nid,
    facilityId,
  })
}

export function openCreateChecklistColumn({ facilityId }: { facilityId: string }) {
  setSideColumnState({ type: 'create_checklist', facilityId })
}

export function openCheckInShipmentColumn(
  selectedShipment?: SelectedShipmentWithStatus,
  currentStep: CheckInShipmentStep = CheckInShipmentStep.INITIAL
) {
  setSideColumnState({ type: 'check_in_shipment', selectedShipment, currentStep })
}

type OpenBookShipmentColumnParams = Omit<BookShipmentSideBarState, 'type' | 'currentStep'> & {
  currentStep?: number
}

export function openBookShipmentColumn(state: OpenBookShipmentColumnParams) {
  GlobalSideColumnStore.update((s) => {
    if (s.sideColumnState?.type !== 'book_shipment') {
      s.sideColumnState = {
        ...state,
        type: 'book_shipment',
        currentStep: state.currentStep ?? BookShipmentStep.CREATE_SINGLE_SENDER_SHIPMENT,
      }
      return
    }

    if (state.selectedPOId !== undefined) {
      s.sideColumnState.selectedPOId = state.selectedPOId
    }

    if (state.selectedShipmentNid !== undefined) {
      s.sideColumnState.selectedShipmentNid = state.selectedShipmentNid
    }

    if (state.showCreateShipmentForm !== undefined) {
      s.sideColumnState.showCreateShipmentForm = state.showCreateShipmentForm
    }

    if (state.selectedSlotId !== undefined) {
      s.sideColumnState.selectedSlotId = state.selectedSlotId
    }

    if (state.currentStep !== undefined) {
      s.sideColumnState.currentStep = state.currentStep
    }
  })
}

export function openVisitDetailsColumn(visitId: Identifier) {
  setSideColumnState({ type: 'visit_details', selectedVisitId: visitId })
}

export function openExtractionRunDetailsColumn(runId: Identifier): void {
  setSideColumnState({ type: 'extraction_run_details', selectedRunId: runId })
}

export function useSelectedSlot() {
  return GlobalSideColumnStore.useState((s) => {
    if (s.sideColumnState?.type === 'edit_slot' || s.sideColumnState?.type === 'book_shipment') {
      return s.sideColumnState.selectedSlotId
    }
    return null
  })
}

export function useDashboardColSpan() {
  const sideColumnState = GlobalSideColumnStore.useState((s) => s.sideColumnState)

  if (sideColumnState) {
    return getMainAndSiderSpans({
      siderWidth: 8,
    })
  }

  return getMainAndSiderSpans({ siderWidth: 0 })
}

registerInDevtools({ GlobalSideColumnStore }, { namespace: 'GlobalSideColumnStore' })

export default GlobalSideColumnStore
